/**
 * MODULE: domain/templates/frontend/api/slideDecksApi.ts
 * PURPOSE: HTTP client for /api/v1/slide-decks (CRUD, publish, analytics, public share).
 * DEPENDENCIES: @shared/lib/frontend/api/config
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 207 / 240 lines
 * RECENT: 2026-05-13 [2.2.972] — **`SlideDeckPatchRequest`** carries optional **`review_state`** (PATCH body unchanged otherwise). [2.2.968] — **`postComment`** body optional **`region_id`**; root **`CHANGELOG` [2.2.968]`**. 2026-04-19 — G25 **`postPresence`**, **`listPresence`**, comments, versions. [2.2.521] — **`SlideDeckHttpError`**, **`recordView`** no-op.
 */

import { API_CONFIG, getAuthHeaders, getAuthHeadersForFormData } from '@shared/lib/frontend/api/config';
import type {
  CloneSlideDeckFromTemplateResponse,
  CloneSlideDeckTemplateRequest,
  SlideDeckAnalyticsResponse,
  SlideDeckCommentsListResponse,
  SlideDeckCommentRow,
  SlideCommentRegion,
  SlideDeckCreateRequest,
  SlideDeckListResponse,
  SlideDeckPatchRequest,
  SlideDeckPublishResponse,
  SlideDeckPresenceResponse,
  SlideDeckRow,
  SlideDeckTemplateListItem,
  SlideDeckVersionsListResponse,
  SaveAsSlideDeckTemplateRequest,
  HubLibraryAssetDetail,
  HubLibraryAssetsListResponse,
  HubLibraryAssetListItem,
  HubLibraryKind,
  SaveHubLibrarySlideRequest,
  SaveHubLibraryBlockRequest,
  TemplateCategory,
  MergePreviewResponse,
  MergeSchemaResponse,
} from '../../contracts/slide_deck.types';

const BASE = `${API_CONFIG.baseURL.replace(/\/$/, '')}/api/v1/slide-decks`;

/** Thrown by `getByShareToken` with HTTP status for share viewer error UI. */
export class SlideDeckHttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'SlideDeckHttpError';
    this.status = status;
  }
}

async function fetchWithAuth<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error((err as { detail?: string }).detail || `HTTP ${response.status}`);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
}

async function fetchWithAuthNoJson(path: string, options: RequestInit = {}): Promise<void> {
  const response = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error((err as { detail?: string }).detail || `HTTP ${response.status}`);
  }
}

export const slideDecksApi = {
  list: (params?: { page?: number; limit?: number; status?: string; created_by?: string }) => {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.status) q.set('status', params.status);
    if (params?.created_by) q.set('created_by', params.created_by);
    const qs = q.toString();
    return fetchWithAuth<SlideDeckListResponse>(qs ? `?${qs}` : '');
  },

  listTemplates: (params?: { category?: TemplateCategory; search?: string }) => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.search?.trim()) q.set('search', params.search.trim());
    const qs = q.toString();
    return fetchWithAuth<{ templates: SlideDeckTemplateListItem[] }>(`/templates${qs ? `?${qs}` : ''}`).then(
      (r) => r.templates
    );
  },

  create: (body: SlideDeckCreateRequest) =>
    fetchWithAuth<SlideDeckRow>('', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  get: (id: string) => fetchWithAuth<SlideDeckRow>(`/${encodeURIComponent(id)}`),

  patch: (id: string, body: SlideDeckPatchRequest) =>
    fetchWithAuth<SlideDeckRow>(`/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  remove: (id: string) =>
    fetchWithAuth<void>(`/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  publish: (id: string) =>
    fetchWithAuth<SlideDeckPublishResponse>(`/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
    }),

  analytics: (id: string) =>
    fetchWithAuth<SlideDeckAnalyticsResponse>(`/${encodeURIComponent(id)}/analytics`),

  postPresence: (id: string, body: { cursor_slide_id: string; color?: string }) =>
    fetchWithAuthNoJson(`/${encodeURIComponent(id)}/presence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cursor_slide_id: body.cursor_slide_id, color: body.color ?? '#6366f1' }),
    }),

  listPresence: (id: string) =>
    fetchWithAuth<SlideDeckPresenceResponse>(`/${encodeURIComponent(id)}/presence`),

  listComments: (id: string, slideId?: string) => {
    const q = slideId ? `?slide_id=${encodeURIComponent(slideId)}` : '';
    return fetchWithAuth<SlideDeckCommentsListResponse>(`/${encodeURIComponent(id)}/comments${q}`);
  },

  postComment: (
    id: string,
    body: { slide_id: string; body: string; mentions?: string[]; region_id?: SlideCommentRegion }
  ) =>
    fetchWithAuth<SlideDeckCommentRow>(`/${encodeURIComponent(id)}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  patchComment: (deckId: string, commentId: string, body: { resolved: boolean }) =>
    fetchWithAuth<SlideDeckCommentRow>(
      `/${encodeURIComponent(deckId)}/comments/${encodeURIComponent(commentId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    ),

  listVersions: (id: string) =>
    fetchWithAuth<SlideDeckVersionsListResponse>(`/${encodeURIComponent(id)}/versions`),

  restoreVersion: (deckId: string, versionId: string) =>
    fetchWithAuth<SlideDeckRow>(
      `/${encodeURIComponent(deckId)}/versions/${encodeURIComponent(versionId)}/restore`,
      { method: 'POST' }
    ),

  saveAsTemplate: (body: SaveAsSlideDeckTemplateRequest) =>
    fetchWithAuth<SlideDeckTemplateListItem>('/save-as-template', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  cloneTemplate: (body: CloneSlideDeckTemplateRequest) =>
    fetchWithAuth<CloneSlideDeckFromTemplateResponse>('/clone-template', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  /** Public share view — no auth headers (optional email query for analytics). */
  getByShareToken: async (token: string, email?: string): Promise<SlideDeckRow> => {
    const q = email ? `?email=${encodeURIComponent(email)}` : '';
    const response = await fetch(
      `${BASE}/share/${encodeURIComponent(token)}${q}`,
      { credentials: 'omit' }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Request failed' }));
      const detail = (err as { detail?: string }).detail || `HTTP ${response.status}`;
      throw new SlideDeckHttpError(response.status, detail);
    }
    return response.json();
  },

  /** Public merge resolution for shared decks — no auth. */
  getShareMergePreview: async (token: string): Promise<MergePreviewResponse> => {
    const response = await fetch(
      `${BASE}/share/${encodeURIComponent(token)}/merge-preview`,
      { credentials: 'omit' }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Request failed' }));
      const detail = (err as { detail?: string }).detail || `HTTP ${response.status}`;
      throw new SlideDeckHttpError(response.status, detail);
    }
    return response.json();
  },

  /** Authenticated merge field catalog. */
  getMergeSchema: () => fetchWithAuth<MergeSchemaResponse>('/merge-schema'),

  /** Authenticated CRM merge preview for builder Data panel. */
  postMergePreview: (crmBinding: Record<string, unknown>) =>
    fetchWithAuth<MergePreviewResponse>('/merge-preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ crm_binding: crmBinding }),
    }),

  /**
   * Reserved for a future POST-only analytics path. Today the gateway records a view on
   * GET /share/{token}; calling this keeps ShareViewer wiring stable without double-counting.
   */
  recordView: async (_token: string): Promise<void> => {
    return;
  },

  listLibraryAssets: (params?: { kind?: HubLibraryKind }) => {
    const q = new URLSearchParams();
    if (params?.kind) q.set('kind', params.kind);
    const qs = q.toString();
    return fetchWithAuth<HubLibraryAssetsListResponse>(`/library-assets${qs ? `?${qs}` : ''}`);
  },

  getLibraryAsset: (assetId: string) =>
    fetchWithAuth<HubLibraryAssetDetail>(`/library-assets/${encodeURIComponent(assetId)}`),

  saveLibrarySlide: (body: SaveHubLibrarySlideRequest) =>
    fetchWithAuth<HubLibraryAssetListItem>('/library-assets/slides', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  saveLibraryBlock: (body: SaveHubLibraryBlockRequest) =>
    fetchWithAuth<HubLibraryAssetListItem>('/library-assets/blocks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  /**
   * Import PPTX as pptx_canonical deck (punched plates + text/media slots).
   * Geometry locked; download via `renderPptx`.
   */
  importPptx: async (file: File, opts?: { title?: string; templateKey?: string }) => {
    const form = new FormData();
    form.append('file', file);
    if (opts?.title) form.append('title', opts.title);
    if (opts?.templateKey) form.append('template_key', opts.templateKey);
    const response = await fetch(`${BASE}/import-pptx`, {
      method: 'POST',
      headers: { ...getAuthHeadersForFormData() },
      credentials: 'include',
      body: form,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Import failed' }));
      throw new Error((err as { detail?: string }).detail || `HTTP ${response.status}`);
    }
    return response.json() as Promise<{
      deck: SlideDeckRow;
      warnings: string[];
      mode: string;
      transformPolicy: string;
      punchOutStrategy?: string;
      slotCount: number;
      builderPath: string;
    }>;
  },

  /** Apply AI `{ slotId → value }` fills on a pptx_canonical deck. */
  fillPptxSlots: async (
    deckId: string,
    fills: Record<string, string | { text?: string; src?: string; pptxFileId?: string | null }>,
  ) => {
    const response = await fetch(`${BASE}/${encodeURIComponent(deckId)}/fill-slots`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ fills }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Fill failed' }));
      throw new Error((err as { detail?: string }).detail || `HTTP ${response.status}`);
    }
    return response.json() as Promise<{
      deck: SlideDeckRow;
      applied: string[];
      skipped: Array<{ slotId?: string; reason?: string }>;
    }>;
  },

  /** High-fidelity PPTX download (content-only patch into working template). */
  renderPptx: async (
    deckId: string,
    body?: { slides?: unknown[] },
  ): Promise<{
    blob: Blob;
    applied: number;
    skipped: number;
    punchOut: string;
    transformPolicy: string;
  }> => {
    const response = await fetch(`${BASE}/${encodeURIComponent(deckId)}/render-pptx`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Render failed' }));
      throw new Error((err as { detail?: string }).detail || `HTTP ${response.status}`);
    }
    const blob = await response.blob();
    return {
      blob,
      applied: Number(response.headers.get('X-Brazn-Patch-Applied') || 0),
      skipped: Number(response.headers.get('X-Brazn-Patch-Skipped') || 0),
      punchOut: response.headers.get('X-Brazn-Punch-Out') || 'exclude_shapes',
      transformPolicy: response.headers.get('X-Brazn-Transform-Policy') || 'content_only',
    };
  },
};
